import { useIntl } from 'react-intl';

import type { PriceCurrency } from '@packages/gql/generated/shopping/graphql';
import { usePriceFormatter } from '@packages/shared/src/hooks/usePriceFormatter/usePriceFormatter';

type PriceProps = {
  price: number | null;
  currency: PriceCurrency;
  free: boolean;
  mandatory: boolean;
  /** the quantity of the line item the service belongs to - the price is multiplied by this value */
  quantity?: number;
};

/**
 * atom for Aplus - renders the service price.
 */
export const Price = ({ price, currency, free, mandatory, quantity }: PriceProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormatter('APLUS');

  if (free) {
    return (
      <span className="text-size-body-sm/8 text-color-success-dark font-bold whitespace-nowrap">
        {mandatory
          ? formatMessage({
              id: 'aplus.inclusive',
              defaultMessage: 'inklusive',
            })
          : formatMessage({
              id: 'aplus.free',
              defaultMessage: 'gratis',
            })}
      </span>
    );
  }

  if (price) {
    const hasMultipleQuantity = typeof quantity === 'number' && quantity > 1;
    const totalPrice = hasMultipleQuantity ? price * quantity : price;

    return (
      <span className="flex flex-col items-end">
        <span className="text-size-body-sm/4 font-bold whitespace-nowrap">
          +{formatPrice(totalPrice, currency)}
        </span>
        {hasMultipleQuantity && (
          <span className="text-size-body-xs text-color-text-secondary whitespace-nowrap">
            {formatMessage(
              { id: 'aplus.pricePerService', defaultMessage: '{price} / Service' },
              { price: formatPrice(price, currency) },
            )}
          </span>
        )}
      </span>
    );
  }

  return null;
};
